import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, Lightbulb, Sparkles } from "lucide-react";
import API from "../lib/api";
import { Section } from "../components/Common";

const GOAL_KEYS = ["energy", "sleep", "stress", "fitness", "nutrition", "confidence", "mental", "hormones", "focus", "performance"];

export default function DoshaTestPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState("");
  const [goals, setGoals] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [funFacts, setFunFacts] = useState([]);
  const [answers, setAnswers] = useState({}); // {q1: 'vata'}
  const [qIdx, setQIdx] = useState(0);
  const [factIdx, setFactIdx] = useState(0);
  const [showFact, setShowFact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load questions when we enter step 3
  useEffect(() => {
    if (step !== 3) return;
    setLoading(true);
    const params = new URLSearchParams({ gender, goal: goals[0] || "" });
    API.get(`/dosha/questions?${params.toString()}`).then(({ data }) => {
      setQuestions(data.questions || []);
      setFunFacts(data.fun_facts || []);
      setQIdx(0);
      setAnswers({});
    }).finally(() => setLoading(false));
  }, [step, gender, goals]);

  const toggleGoal = (g) => {
    setGoals((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : prev.length >= 3 ? prev : [...prev, g]);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        gender,
        goals,
        answers: Object.entries(answers).map(([qid, choice]) => ({ question_id: qid, choice })),
        language: i18n.language,
      };
      const { data } = await API.post("/dosha/submit", payload);
      navigate(`/dosha/result/${data.result_id}`);
    } finally {
      setSubmitting(false);
    }
  };

  const currentQ = questions[qIdx];
  const progress = useMemo(() => {
    if (step === 3 && questions.length) return Math.round(((qIdx + (showFact ? 1 : 0)) / questions.length) * 100);
    if (step === 1) return 5;
    if (step === 2) return 18;
    return 0;
  }, [step, qIdx, showFact, questions.length]);

  const answerCurrent = (choice) => {
    if (!currentQ) return;
    setAnswers((prev) => ({ ...prev, [currentQ.id]: choice }));
    setFactIdx(Math.floor(Math.random() * funFacts.length));
    setShowFact(true);
  };

  const nextAfterFact = () => {
    setShowFact(false);
    if (qIdx + 1 < questions.length) {
      setQIdx(qIdx + 1);
    } else {
      submit();
    }
  };

  return (
    <div data-testid="page-dosha">
      <section className="relative hero-bg">
        <div className="app-container pt-14 pb-10">
          <div className="eyebrow">{t("dosha.eyebrow")}</div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05] max-w-[18ch]">{t("dosha.title")}</h1>
          <p className="text-white/75 mt-4 max-w-[58ch] leading-relaxed body-copy">{t("dosha.intro")}</p>
          <div className="mt-7 h-1 w-full rounded-full bg-white/10 overflow-hidden" aria-label={t("dosha.progress_aria")}>
            <div className="h-full bg-gradient-to-r from-[#7A5F2A] via-[#C8A24A] to-[#E2C77A] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="dosha-progress" />
          </div>
        </div>
      </section>

      <div className="app-container py-10">
        {/* STEP 1 */}
        {step === 1 && (
          <div data-testid="dosha-step-1">
            <h2 className="font-display text-2xl md:text-3xl">{t("dosha.step1_title")}</h2>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              {[
                { v: "male", label: t("dosha.gender_male") },
                { v: "female", label: t("dosha.gender_female") },
                { v: "nonbinary", label: t("dosha.gender_nb") },
              ].map((g) => (
                <button key={g.v} onClick={() => { setGender(g.v); setStep(2); }} className={`card-surface p-6 text-start ${gender === g.v ? "border-[#C8A24A]" : ""}`} data-testid={`gender-${g.v}`}>
                  <Sparkles className="w-5 h-5 gold-text" />
                  <div className="mt-2 font-display text-xl">{g.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div data-testid="dosha-step-2">
            <div className="flex items-center gap-3 mb-1">
              <button onClick={() => setStep(1)} className="btn-ghost inline-flex items-center gap-1 text-sm"><ArrowLeft className="w-4 h-4" /> {t("common.back")}</button>
            </div>
            <h2 className="font-display text-2xl md:text-3xl">{t("dosha.step2_title")}</h2>
            <p className="text-white/65 mt-1">{t("dosha.step2_hint")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-6" data-testid="goals-grid">
              {GOAL_KEYS.map((g) => (
                <button key={g} onClick={() => toggleGoal(g)} className={`card-surface p-4 text-start ${goals.includes(g) ? "border-[#C8A24A]" : ""}`} data-testid={`goal-${g}`}>
                  <div className="text-xs uppercase tracking-[0.2em] gold-text">{t("dosha.goal_label")}</div>
                  <div className="font-display text-lg mt-1">{t(`dosha.goals.${g}`)}</div>
                </button>
              ))}
            </div>
            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm text-white/60">{t("dosha.selected_count", { n: goals.length })}</div>
              <button onClick={() => setStep(3)} disabled={goals.length === 0} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="goals-next">{t("common.next")} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div data-testid="dosha-step-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl md:text-3xl">{t("dosha.step3_title")}</h2>
              {questions.length > 0 && <div className="text-sm text-white/60">{Math.min(qIdx + 1, questions.length)} / {questions.length}</div>}
            </div>

            {loading && <div className="text-white/70 mt-6">{t("common.loading")}</div>}

            {!loading && !showFact && currentQ && (
              <div className="mt-6 card-surface p-6 md:p-8" data-testid="current-question">
                <div className="text-xs uppercase tracking-[0.24em] gold-text">{t("dosha.question_label", { n: qIdx + 1 })}</div>
                <div className="font-display text-xl md:text-2xl mt-2">{currentQ.text}</div>
                <div className="mt-5 space-y-2">
                  {currentQ.options.map((o, i) => (
                    <button key={i} onClick={() => answerCurrent(o.choice)} className="w-full text-start p-4 rounded-lg border border-white/10 hover:border-[#C8A24A] hover:bg-white/5 transition-colors" data-testid={`q-opt-${o.choice}-${i}`}>
                      <div className="text-sm text-white/85">{o.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!loading && showFact && funFacts.length > 0 && (
              <div className="mt-6 card-surface p-6 md:p-8" data-testid="fun-fact">
                <div className="flex items-center gap-3"><Lightbulb className="w-5 h-5 gold-text" /><div className="text-xs uppercase tracking-[0.24em] gold-text">{t("dosha.fun_fact")}</div></div>
                <div className="font-display text-xl md:text-2xl mt-3">{funFacts[factIdx].text}</div>
                <div className="mt-6 flex justify-end">
                  <button onClick={nextAfterFact} disabled={submitting} className="btn-primary inline-flex items-center gap-2" data-testid="next-after-fact">
                    {qIdx + 1 < questions.length ? t("common.next") : (submitting ? "…" : t("common.submit"))} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
